import { useState, useEffect, useCallback, useMemo } from 'react';
import { registerLocale } from 'react-datepicker';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { eachMonthOfInterval, format, subYears, addYears } from 'date-fns';
import { ja } from 'date-fns/locale/ja';
import { useAtom } from 'jotai';

import { getPickupDropoffReservations } from '@/api/getPickupDropoffReservations';
import { getVehicles } from '@/api/getVehicles';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import { NavigationButtons } from '@/components/reservations/NavigationButtons';
import PickupDropoffReservationsTable from '@/components/reservations/transport/PickupDropoffReservationsTable';
import UpdatePickupDropoffReservationDialog from '@/components/reservations/transport/UpdatePickupDropoffReservationDialog';
import SelectField from '@/components/SelectField/SelectField';
import { NotificationAtom } from '@/jotai/jotai';
import { PickupDropoffReservation } from '@/types/pickupDropoff';
import { Vehicle } from '@/types/vehicle';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('ja', ja);

type SearchParams = {
  yearMonth: string;
  vehicleID: string;
};

export const ReservationsTransportPage = () => {
  const { pathname: pathName } = useLocation();
  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: '送迎予約一覧' }];
  const email = localStorage.getItem('email') || '';

  const [pickupDropoffReservations, setPickupDropoffReservations] = useState<
    PickupDropoffReservation[]
  >([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isOpenUpdateDialog, setIsOpenUpdateDialog] = useState(false);

  const [_, setNotification] = useAtom(NotificationAtom);

  const { control } = useForm<SearchParams>({
    defaultValues: {
      yearMonth: format(new Date(), 'yyyy-MM'),
      vehicleID: 'all',
    },
    mode: 'onChange',
  });

  const [yearMonth, vehicleID] = useWatch({
    control,
    name: ['yearMonth', 'vehicleID'],
  });

  const vehicleItems = useMemo(
    () =>
      [{ value: 'all', label: '全て' }].concat(
        vehicles.map((vehicle) => ({
          value: vehicle.id.toString(),
          label: vehicle.name,
        }))
      ),
    [vehicles]
  );

  useEffect(() => {
    const [year, month] = yearMonth.split('-');

    const fetchPickupDropoffReservations = async () => {
      try {
        const result = await getPickupDropoffReservations(
          email,
          year,
          month,
          vehicleID === 'all' ? undefined : vehicleID
        );
        if (result.status === 200) {
          const data = result.result;

          setPickupDropoffReservations(data);
          console.log(data);
        }
      } catch (error) {
        setPickupDropoffReservations([]);
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: '送迎予約の取得に失敗しました',
          message: '送迎予約の取得に失敗しました',
        });
      }
    };

    const fetchVehicles = async () => {
      try {
        const result = await getVehicles(email);
        if (result.status === 200) {
          const data = result.result;

          setVehicles(data);
          console.log(data);
        }
      } catch (error) {
        setVehicles([]);
        console.log(error);
      }
    };

    fetchPickupDropoffReservations();
    fetchVehicles();
  }, [yearMonth, vehicleID]);

  const getYearMonths = () => {
    const yearMonths = eachMonthOfInterval({
      start: subYears(new Date(), 1),
      end: addYears(new Date(), 1),
    });

    return yearMonths.map((yearMonth) => ({
      value: format(yearMonth, 'yyyy-MM'),
      label: format(yearMonth, 'yyyy年MM月'),
    }));
  };

  const handleRefresh = useCallback(async () => {
    const [year, month] = yearMonth.split('-');
    try {
      const result = await getPickupDropoffReservations(
        email,
        year,
        month,
        vehicleID === 'all' ? undefined : vehicleID
      );
      if (result.status === 200) {
        const data = result.result;

        setPickupDropoffReservations(data);
        console.log(data);
      }
    } catch (error) {
      setPickupDropoffReservations([]);
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: '送迎予約の取得に失敗しました',
        message: '送迎予約の取得に失敗しました',
      });
    }
  }, [yearMonth, vehicleID, email]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <NavigationButtons />
      <div className="mt-5 flex flex-col justify-start gap-8 bg-white">
        <div className="flex max-w-sm flex-row justify-start space-x-2">
          <SelectField
            name="yearMonth"
            control={control}
            items={getYearMonths()}
            defaultValue={format(new Date(), 'yyyy-MM')}
          />
          <SelectField
            name="vehicleID"
            control={control}
            items={vehicleItems}
            defaultValue={'all'}
          />
          <Button
            size="lg"
            type="submit"
            variant="solid-fill"
            onClick={() => setIsOpenUpdateDialog(true)}
          >
            配車登録
          </Button>
        </div>
        <div>
          <PickupDropoffReservationsTable
            reservations={pickupDropoffReservations}
            vehicles={vehicles.filter(
              (vehicle) => vehicle.id === Number(vehicleID) || vehicleID === 'all'
            )}
          />
        </div>
      </div>
      <UpdatePickupDropoffReservationDialog
        isOpen={isOpenUpdateDialog}
        setIsOpen={setIsOpenUpdateDialog}
        afterSubmit={() => handleRefresh()}
        vehicles={vehicles}
        reservations={pickupDropoffReservations}
      />
    </div>
  );
};
